import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { LoginResponse } from '@/api/types/auth';
import downwardIcon from '@/assets/icons/downwardIcon.svg';
import upwardIcon from '@/assets/icons/upwardIcon.svg';
import { Button } from '@/components/ui/button';
import { Nudge } from '@/components/ui/nudge';
import { queryKeys } from '@/config/query/queryKeys';
import { type FilterMode, useOfficeContextStore } from '@/utils/stores/useOfficeContextStore';
import { cn } from '@/utils/utils';

export type TabType = 'email-dropdown' | 'files-dropdown';

interface TabsProps {
  disabled?: boolean;
  isEmailContextDialogOpen?: boolean;
  isFilesUploadedListDropdownOpen?: boolean;
  onValueChange?: (value: TabType) => void;
  showFilesUploaded?: boolean;
  value?: TabType;
}

export function Tabs({
  disabled = false,
  isEmailContextDialogOpen = false,
  isFilesUploadedListDropdownOpen = false,
  onValueChange,
  showFilesUploaded = false,
  value = 'email-dropdown',
}: TabsProps) {
  const { getCurrentFilterMode } = useOfficeContextStore();
  const selectedFolderName = useOfficeContextStore((s) => s.selectedFolderName);
  const getIcon = (isOpen: boolean) => (isOpen ? upwardIcon : downwardIcon);

  const { t } = useTranslation();

  const { data: loginUserData } = useQuery<LoginResponse['user']>({
    enabled: false,
    queryKey: queryKeys.auth.user(),
  });
  const showAllMailsNudge =
    (loginUserData?.ragSearchEnabled ?? false) && !!loginUserData?.initialSyncCompletedAt;

  // Get dynamic label based on current filter mode (user's selection or default)
  const getEmailContextLabel = (): string => {
    const filterMode: FilterMode = getCurrentFilterMode();
    switch (filterMode) {
      case 'currentEmailThread':
        return t('assistant.tabs.currentEmailThread');
      case 'allMails':
        return t('assistant.tabs.allMails');
      case 'custom':
        return t('assistant.tabs.custom');
      case 'folder':
        return selectedFolderName
          ? t('assistant.tabs.folderWithName', { name: selectedFolderName })
          : t('assistant.tabs.folder');
      default:
        return t('assistant.tabs.currentEmailThread');
    }
  };

  // Handle tab click - we control everything manually
  const handleTabClick = (tabValue: TabType) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      onValueChange?.(tabValue);
    }
  };

  return (
    <div className="flex w-full items-end justify-end py-0 pr-2" data-tabs-container>
      <div className="flex items-end gap-0 whitespace-nowrap">
        {/* We don't use Radix Tabs here - just styled buttons */}
        <div className="flex items-end">
          {/* Email Dropdown Container - appears first on the left */}
          <div
            className={cn(
              'box-border flex items-end bg-card px-1 pt-1 pb-0',
              showFilesUploaded ? 'rounded-tl-xl' : 'rounded-t-xl',
              isEmailContextDialogOpen && [
                'border-t-[0.5px] border-r-[0.5px] border-b-0 border-l-[0.5px]',
                'border-solid border-info',
                'bg-info-bg',
              ],
            )}
          >
            <Nudge
              description={t('assistant.emailContext.allMailsNudge.tabDescription')}
              disabled={!showAllMailsNudge}
              id="all-emails-context"
              side="top"
              title={t('assistant.emailContext.allMailsNudge.tabTitle')}
            >
              <Button
                className={cn(
                  "flex h-auto cursor-pointer items-center gap-1.5 border-0 bg-transparent px-3 py-1 font-['Inter'] text-[12px] leading-[170%] font-normal tracking-[0%] whitespace-nowrap text-foreground shadow-none transition-colors hover:bg-transparent focus:outline-none focus-visible:ring-0 disabled:cursor-not-allowed",
                  showFilesUploaded ? 'rounded-tl-xl' : 'rounded-t-xl',
                  isEmailContextDialogOpen && 'bg-info-bg hover:bg-info-bg',
                )}
                disabled={disabled}
                onClick={handleTabClick('email-dropdown')}
                size="sm"
                variant="ghost"
              >
                <span>{getEmailContextLabel()}</span>
                <img
                  alt=""
                  className={cn(
                    'icon-themed h-3 w-3',
                    value === 'email-dropdown' ? 'opacity-100' : 'opacity-40',
                  )}
                  src={getIcon(isEmailContextDialogOpen)}
                />
              </Button>
            </Nudge>
          </div>

          {/* Divider */}
          {showFilesUploaded && (
            <div
              className={cn(
                'box-border h-full w-[1px] self-stretch',
                isEmailContextDialogOpen || isFilesUploadedListDropdownOpen
                  ? [
                      'bg-info',
                      'border-t-0 border-r-[0.5px] border-b-0 border-l-0',
                      'border-solid border-info',
                    ]
                  : 'bg-border-subtle',
              )}
            />
          )}

          {/* Files Dropdown Container - always at rightmost */}
          {showFilesUploaded && (
            <div
              className={cn(
                'box-border flex items-end rounded-tr-xl bg-card px-1 pt-1 pb-0',
                isFilesUploadedListDropdownOpen && [
                  'border-t-[0.5px] border-r-[0.5px] border-b-0 border-l-[0.5px]',
                  'border-solid border-info',
                  'bg-info-bg',
                ],
              )}
            >
              <Button
                className={cn(
                  'flex h-auto cursor-pointer items-center gap-1.5 rounded-tr-xl border-0 bg-transparent px-3 py-1 font-["Inter"] text-[12px] leading-[170%] font-normal tracking-[0%] whitespace-nowrap text-foreground shadow-none transition-colors hover:bg-transparent focus:outline-none focus-visible:ring-0 disabled:cursor-not-allowed',
                  isFilesUploadedListDropdownOpen && 'bg-info-bg hover:bg-info-bg',
                )}
                disabled={disabled}
                onClick={handleTabClick('files-dropdown')}
                size="sm"
                variant="ghost"
              >
                <span>{t('assistant.tabs.filesUploaded')}</span>
                <img
                  alt=""
                  className={cn(
                    'icon-themed h-3 w-3',
                    value === 'files-dropdown' ? 'opacity-100' : 'opacity-40',
                  )}
                  src={getIcon(isFilesUploadedListDropdownOpen)}
                />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
